import React from 'react'

type Player = { id: string; name: string }
type Tournament = { id: string; name: string }

type Row = {
  id?: string
  date: string
  playerName: string
  amount: number
  reason?: string
  comment?: string
  playerId?: string
}

export default function TournamentCancellationsListModal({
  tournament,
  level,
  open,
  onClose,
}: {
  tournament: Tournament
  level: string
  open: boolean
  onClose: () => void
}) {
  const [rows, setRows] = React.useState<Row[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string>('')
  const refreshRef = React.useRef<() => Promise<void>>()
  const formatDate = React.useCallback((isoLike: string) => {
    if (!isoLike) return ''
    const s = String(isoLike)
    // support strings like '2025-10-06' or Date
    const d = new Date(s)
    if (!isNaN(d.getTime())) {
      const dd = String(d.getDate()).padStart(2, '0')
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const yyyy = d.getFullYear()
      return `${dd}.${mm}.${yyyy}`
    }
    // fallback for plain 'yyyy-mm-dd'
    const parts = s.slice(0,10).split('-')
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`
    return s
  }, [])

  React.useEffect(() => {
    if (!open) return
    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        setError('')
        setRows([])
        // 1) участники турнира (для имён)
        const resPlayers = await fetch(`/api/tournaments/${tournament.id}/players`)
        const dataPlayers = await resPlayers.json()
        const players: Player[] = Array.isArray(dataPlayers?.items)
          ? dataPlayers.items.map((p: any) => ({ id: p.id, name: p.name || p.playerName || 'Игрок' }))
          : []
        const idToName = new Map(players.map(p => [p.id, p.name]))

        // 2) прямой список отмен по турниру
        const resCanc = await fetch(`/api/player-cancellations?tournamentId=${encodeURIComponent(tournament.id)}`)
        const dataCanc = await resCanc.json()
        const cancItems = Array.isArray(dataCanc?.items) ? dataCanc.items : []

        // добираем отсутствующие имена игроков по id
        const missingIds = Array.from(new Set(cancItems.map((c: any) => c.playerId).filter((pid: string)=>!idToName.get(pid))))
        if (missingIds.length) {
          await Promise.all(missingIds.map(async (pid) => {
            try {
              const r = await fetch(`/api/players/${pid}`)
              if (r.ok) {
                const d = await r.json()
                const nm = d?.name || d?.player?.name
                if (nm) idToName.set(pid, nm)
              }
            } catch {}
          }))
        }

        let flat: Row[] = cancItems
          .map((c: any) => ({
            id: c.id,
            date: formatDate(c.createdAt || ''),
            playerName: idToName.get(c.playerId) || c.playerId,
            amount: Number(c.amount || 0),
            reason: c.reason,
            comment: c.description,
            playerId: c.playerId,
          }))

        // если всё ещё пусто — fallback на детализации (исторические записи)
        if (!flat.length) {
          const tasks = players.map(async (p) => {
            const params = new URLSearchParams({ tournamentId: tournament.id })
            const res = await fetch(`/api/reports/player-settlements-movements/${p.id}/details?${params.toString()}`)
            const data = await res.json()
            const items = Array.isArray(data?.items) ? data.items : []
            const cancels = items.filter((it: any) => it.type === 'ADJUSTMENT')
            return cancels.map((c: any) => ({
              id: c.id,
              date: formatDate(c.date || ''),
              playerName: p.name,
              amount: Number(c.amount || 0),
              reason: c.reason,
              comment: c.document,
              playerId: p.id,
            }) as Row)
          })
          const settled = await Promise.all(tasks)
          flat = settled.flat()
        }

        if (!cancelled) setRows(flat.sort((a, b) => (a.date > b.date ? -1 : 1)))
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Ошибка загрузки')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    // make reload accessible for inner actions
    refreshRef.current = load
    return () => { cancelled = true }
  }, [open, tournament.id, level])

  const handleEdit = React.useCallback((id?: string, playerId?: string) => {
    if (!id) return
    try {
      window.dispatchEvent(new CustomEvent('open-adjustment-edit', { detail: { id, tournamentId: tournament.id, playerId } }))
      onClose()
    } catch {}
  }, [onClose, tournament.id])

  const handleDelete = React.useCallback(async (id?: string) => {
    if (!id) return
    if (!confirm('Удалить отмену?')) return
    try {
      const res = await fetch(`/api/player-cancellations/${id}`, { method: 'DELETE' })
      if (!res.ok) throw new Error('Ошибка удаления')
      // reload list
      await (refreshRef.current ? refreshRef.current() : Promise.resolve())
    } catch (e) {
      console.error(e)
      alert('Не удалось удалить запись')
    }
  }, [])

  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-2xl rounded shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-gray-900">Отмены по уровню {level}</div>
          <button onClick={onClose} className="px-3 py-1 border rounded text-sm">Закрыть</button>
        </div>

        {loading && (
          <div className="text-gray-600 text-sm">Загрузка…</div>
        )}
        {!!error && (
          <div className="text-red-600 text-sm mb-2">{error}</div>
        )}

        {!loading && rows.length === 0 && (
          <div className="text-gray-600 text-sm">Нет отмен.</div>
        )}

        {!loading && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="text-xs text-gray-500">
                  <th className="py-1 px-2 text-center">Дата</th>
                  <th className="py-1 px-2 text-center">Игрок</th>
                  <th className="py-1 px-2 text-center">Сумма</th>
                  <th className="py-1 px-2 text-center">Комментарий</th>
                  <th className="py-1 px-2 text-center">Действия</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.id || r.playerName}-${r.date}`} className="text-sm">
                    <td className="py-1 px-2">{r.date}</td>
                    <td className="py-1 px-2">{r.playerName}</td>
                    <td className="py-1 px-2 text-right">{r.amount.toFixed(2)}</td>
                    <td className="py-1 px-2">{r.comment || ''}</td>
                    <td className="py-1 px-2">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          title="Редактировать"
                          className="text-blue-600 hover:text-blue-800"
                          onClick={() => handleEdit(r.id, r.playerId)}
                        >
                          {/* pencil icon */}
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                        </button>
                        <button
                          title="Удалить"
                          className="text-red-600 hover:text-red-800"
                          onClick={() => handleDelete(r.id)}
                        >
                          {/* trash icon */}
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}


